// web/src/components/FriendCombobox.jsx
import { useMemo, useState, useId, useRef, useEffect } from "react";
import { rankFriends } from "../lib/data/recent-invitees";
import { getUserIdentity } from "../lib/identity";
import { UserAvatar } from "./ui/avatar";
import { UserIdentity } from "./UserIdentity";

const MAX_DROPDOWN = 50;

// Privacy: match ONLY the user's resolved public identity — their display name
// and their CHOSEN public identifier (Discord / @username / email-by-choice).
// Never match the raw email of someone whose public identifier isn't email, so
// a hidden email can't be confirmed or surfaced through search.
function matches(friend, query) {
  if (!query) return true;
  const q = query.toLowerCase();
  const { displayName, publicIdentifier } = getUserIdentity(friend);
  return [displayName, publicIdentifier]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(q));
}

export function FriendCombobox({
  friends = [],
  recentInvitees = [],
  inputValue = "",
  onInputChange,
  onSelect,
  onSubmitText,
  placeholder = "Add email, Discord username, or @username",
  showAvatars = true,
  maxVisible = 8,
  label = "Recommended (from friends)",
  inputId,
  // When true (default), choosing a friend clears the input — correct for
  // "add immediately" surfaces (InvitePanel). Set false for surfaces that want
  // a pick to FILL the field for review before submitting (InviteMemberModal).
  clearOnSelect = true,
}) {
  const listboxId = useId();
  const reactInputId = useId();
  const resolvedInputId = inputId || reactInputId;
  // `open` is the SOLE source of dropdown visibility so Escape/blur can always
  // dismiss it (a non-empty query must NOT force it open).
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const blurTimer = useRef(null);
  const activeOptionRef = useRef(null);

  const ranked = useMemo(
    () => rankFriends(friends, recentInvitees),
    [friends, recentInvitees]
  );
  const query = inputValue.trim();
  const filtered = useMemo(
    () => (query ? ranked.filter((f) => matches(f, query)) : ranked).slice(0, MAX_DROPDOWN),
    [ranked, query]
  );
  const quickChips = useMemo(() => ranked.slice(0, maxVisible), [ranked, maxVisible]);
  const listOpen = open && filtered.length > 0;

  // Keep the highlighted option scrolled into view during keyboard nav.
  useEffect(() => {
    if (listOpen && activeIndex >= 0 && typeof activeOptionRef.current?.scrollIntoView === "function") {
      activeOptionRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [activeIndex, listOpen]);

  // Avoid setting state after unmount via the blur timeout.
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  const select = (email) => {
    onSelect?.(email);
    if (clearOnSelect) onInputChange?.("");
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (event.key === "Enter") {
      if (open && activeIndex >= 0 && filtered[activeIndex]) {
        event.preventDefault();
        select(filtered[activeIndex].email);
      } else if (onSubmitText) {
        event.preventDefault();
        onSubmitText(inputValue);
      }
    } else if (event.key === "Escape") {
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const optionId = (index) => `${listboxId}-opt-${index}`;

  return (
    <div className="mt-4">
      {quickChips.length > 0 && !query && (
        <>
          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">
            {label}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {quickChips.map((entry) => (
              <button
                key={entry.email}
                type="button"
                onClick={() => select(entry.email)}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary dark:border-slate-700 dark:bg-slate-900 dark:hover:bg-slate-700"
              >
                {showAvatars && (
                  <UserAvatar user={entry} email={entry.email} src={entry.avatar} size={18} />
                )}
                + <UserIdentity user={entry} />
              </button>
            ))}
          </div>
        </>
      )}

      <div className="relative mt-3">
        <div className="flex gap-2">
          <input
            id={resolvedInputId}
            role="combobox"
            aria-expanded={listOpen}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={
              listOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined
            }
            className="flex-1 rounded-full border border-slate-200 px-4 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            placeholder={placeholder}
            value={inputValue}
            onChange={(event) => {
              onInputChange?.(event.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => {
              if (query.length > 0) setOpen(true);
            }}
            onBlur={() => {
              clearTimeout(blurTimer.current);
              blurTimer.current = setTimeout(() => setOpen(false), 120);
            }}
            onKeyDown={handleKeyDown}
          />
          {onSubmitText && (
            <button
              type="button"
              onClick={() => onSubmitText(inputValue)}
              className="rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary dark:border-slate-600 dark:hover:bg-slate-700"
            >
              Add
            </button>
          )}
        </div>

        {listOpen && (
          <ul
            role="listbox"
            id={listboxId}
            className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-2xl border border-slate-200 bg-white p-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
          >
            {filtered.map((entry, index) => (
              <li
                key={entry.email}
                id={optionId(index)}
                ref={index === activeIndex ? activeOptionRef : null}
                role="option"
                aria-selected={index === activeIndex}
                onMouseDown={(event) => {
                  event.preventDefault();
                  select(entry.email);
                }}
                onMouseEnter={() => setActiveIndex(index)}
                className={`flex cursor-pointer items-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${
                  index === activeIndex
                    ? "bg-slate-100 dark:bg-slate-700"
                    : "hover:bg-slate-50 dark:hover:bg-slate-800"
                }`}
              >
                {showAvatars && (
                  <UserAvatar user={entry} email={entry.email} src={entry.avatar} size={18} />
                )}
                <UserIdentity user={entry} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
